import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { Subscription, take } from 'rxjs';
import {
  KpiReportFilter,
  ReportApiResponse,
  ReportRequestParams,
} from 'src/app/common/dtos/reports.dto';
import { translateKpiReportFilterFromFilterDTO } from 'src/app/common/dtos/translators/report-filters.translator';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { selectOptionsFromEnumCapitalized } from 'src/app/common/utilities/enum-helpers';
import { checkIfB2b, checkIfE2L } from 'src/app/common/utilities/role-helpers';
import { ChartColorsAll } from '../../shared/components/reports/report-controller/series-helper';
import { ChartType } from '../../shared/enums/chart-type.enum';
import { stripHtmlForId } from '../../shared/helpers/html.utilities';
import { ProformsFormListService } from '../../shared/services/proforms/proforms-formlist.service';
import { ReportPageFiltersService } from '../../shared/services/report/report-page-filters.service';
import { FetchLastUpdatedDate } from '../../shared/state/reporting/reporting.actions';
import { ReportingState } from '../../shared/state/reporting/reporting.state';
import {
  ProFormAnonymity,
  ProFormsAPIResponse,
  ProFormType,
} from '../../shared/types/responses/proforms.responses';

@Component({
  selector: 'app-reporting-proforms',
  templateUrl: './reporting-proforms.component.html',
  styleUrls: ['./reporting-proforms.component.scss'],
  standalone: false,
})
export class ReportingProformsComponent implements OnInit, OnDestroy {
  subs: Subscription[] = [];

  user: User;

  loading = false;

  canFilterByDistrict = false;

  chartColors = ChartColorsAll;

  filters: KpiReportFilter[];

  proFormsList: ProFormsAPIResponse[];

  districtDisplayName = '';

  chartTypes = ChartType;

  denominatorCount = 0;

  blankSubmissionScount = -1;

  proFormDistrictIds: number[];

  proformTypes: { key: string; value: string }[];

  selectedType: ProFormType = ProFormType.SURVEY;

  selectedProForm: ProFormsAPIResponse | null = null;

  selectedBasicProForm: ProFormsAPIResponse | null = null;

  lastUpdatedDate: string;

  questionTitles: string[];

  optionTypes: string[];

  observedAnswersParams: ReportRequestParams[];

  textQuestionIds: string[];

  textQuestionTitles: string[];

  textQuestionTypes: string[];

  textAnswersParams: ReportRequestParams[];

  quizSummaryParams: ReportRequestParams;

  formSubmissionsParams: ReportRequestParams;

  questionsOrder: string[];

  disableDownload = true;

  ProFormAnonymity = ProFormAnonymity;

  calibrationRequestParams: ReportRequestParams;

  formHasCalibration = false;

  constructor(
    private store: Store,
    private reportFiltersService: ReportPageFiltersService,
    private proFormsListService: ProformsFormListService
  ) {
    const updatedDate$ = this.store.select(ReportingState.getUpdatedDate);
    this.subs.push(
      updatedDate$.subscribe((date) => {
        this.lastUpdatedDate = date;
      })
    );
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
    this.canFilterByDistrict = checkIfE2L(this.user) || checkIfB2b(this.user);
  }

  ngOnInit() {
    this.proformTypes = selectOptionsFromEnumCapitalized(ProFormType);

    this.subs.push(
      this.reportFiltersService.filters.pipe().subscribe((filters) => {
        if (filters.proFormFilter && !this.selectedProForm) {
          this.selectedProForm = filters.proFormFilter;
          this.disableDownload = false;
          this.getQuestionOrder();
        }

        if (filters.schoolYearFilter) {
          this.filters = translateKpiReportFilterFromFilterDTO(filters);
          if (filters.allAssignedSchools) {
            this.filters = this.filters.filter(
              (filter) => filter.codename !== 'school'
            );
            this.filters.push({
              codename: 'school',
              title: 'School',
              value: ['all'],
            });
          }
          this.loadReports();
        }

        if (filters.districtFilter) {
          this.proFormDistrictIds = [];
          this.districtDisplayName = '';
          const lastIndex = filters.districtFilter.length - 1;
          filters.districtFilter.forEach((district, index) => {
            this.proFormDistrictIds.push(district.id);
            this.districtDisplayName += district.title;
            if (index !== lastIndex) {
              this.districtDisplayName += ', ';
            }
          });
          this.getForms();
        }
      })
    );
  }

  getForms() {
    this.store.dispatch(new FetchLastUpdatedDate());
    this.loading = true;

    if (this.user) {
      let totalCount = 0;
      let loadCount = 0;
      const tempForms: ProFormsAPIResponse[] = [];
      this.proFormsList = [];
      this.proFormDistrictIds.forEach((districtId) => {
        totalCount += 1;
        this.proFormsListService
          .getFormsSimple(districtId, 1000)
          .subscribe((response) => {
            take(1);
            loadCount += 1;
            tempForms.push(...response.items);
            if (loadCount === totalCount) {
              tempForms.sort((a, b) => (a.title > b.title ? 0 : 1));
              tempForms.forEach((proForm) => {
                if (
                  !this.proFormsList.length ||
                  this.proFormsList.filter((f) => f.id === proForm.id)
                    .length === 0
                ) {
                  this.proFormsList.push(proForm);
                }
              });
              this.proFormsList = this.proFormsList.filter(
                (form) => form.type === this.selectedType
              );
              this.disableDownload = false;

              this.loading = false;
            }
          });
      });
    }
  }

  onTypeChange(type: string) {
    const typeMapping: { [key: string]: ProFormType } = {
      quiz: ProFormType.QUIZ,
      survey: ProFormType.SURVEY,
      observation: ProFormType.OBSERVATION,
      calibration: ProFormType.CALIBRATION,
    };

    const newType = typeMapping[type];

    if (!newType || this.selectedType === newType) {
      return;
    }

    this.selectedProForm = null;
    this.selectedType = newType;
    this.getForms();
  }

  updateProFormSelect(tempForm: ProFormsAPIResponse) {
    if (tempForm) {
      this.proFormsListService
        .getFormById(tempForm.id)
        .subscribe((fullForm) => {
          this.selectedProForm = fullForm.item;
          this.reportFiltersService.setSelectedProForm(fullForm.item);
          this.disableDownload = false;
          if (fullForm.anonymity === ProFormAnonymity.REQUIRED) {
            this.reportFiltersService.removeFilter('schoolFilter');
            this.reportFiltersService.removeFilter('cohortFilter');
            this.reportFiltersService.removeFilter('participants');
          }
          this.getQuestionOrder();
        });
    } else {
      this.reportFiltersService.removeFilter('proFormFilter');
      this.selectedProForm = null;
      this.disableDownload = true;
    }
  }

  getQuestionOrder() {
    this.questionsOrder = [];
    this.selectedProForm?.sections?.[0]?.questions?.forEach((question) => {
      this.questionsOrder.push(question.id.toString());
    });
  }

  // eslint-disable-next-line class-methods-use-this
  getQuestionId(params: ReportRequestParams) {
    return params.filters.find(
      (filter) => filter.codename === 'proform_question_id'
    )?.value[0];
  }

  downloadCSV(): void {
    this.disableDownload = true;
    this.reportFiltersService.queueReportDownload('proforms_reports');
  }

  loadReports() {
    if (this.reportFiltersService.currentFilters.proFormFilter) {
      this.store.dispatch(new FetchLastUpdatedDate());
      this.getQuestionOptions();
      this.getCalibrationReport();
    }
  }

  getQuestionOptions() {
    this.observedAnswersParams = [];
    this.questionTitles = [];
    this.optionTypes = [];
    this.textQuestionIds = [];
    this.textQuestionTitles = [];
    this.textQuestionTypes = [];
    this.textAnswersParams = [];

    if (this.selectedProForm) {
      this.getQuizSubmissions();

      if (this.selectedProForm.type === ProFormType.QUIZ) {
        this.createQuizSummaryParams();
      }
      this.selectedProForm.sections?.forEach((section) => {
        section.questions?.forEach((question) => {
          if (
            question.field_type === 'checkbox' ||
            question.field_type === 'radio' ||
            question.field_type === 'scale' ||
            question.field_type === 'dropdown' ||
            question.field_type === 'rubric'
          ) {
            this.questionTitles.push(question.question);
            this.optionTypes.push(question.field_type);
            this.observedAnswersParams.push({
              dimension: 'proform_question_answer_option_name',
              dataset: 'fct_proform_submission_answer',
              measure: {
                type: 'count',
                field: '*',
              },
              filters: [
                ...this.filters,
                {
                  codename: 'proform_form_id',
                  value: [question.proform_form_id.toString()],
                },
                {
                  codename: 'proform_question_id',
                  value: [question.id.toString()],
                },
              ],
              ...(question.field_type === 'rubric' ? { group: 'level' } : {}),
            });
          } else if (
            question.field_type === 'text' ||
            question.field_type === 'textarea' ||
            question.field_type === 'attachment' ||
            question.field_type === 'contentblock'
          ) {
            this.textQuestionTitles.push(question.question || 'Text Question');
            this.textQuestionTypes.push(question.field_type);
            this.textQuestionIds.push(question.id.toString());
            this.textAnswersParams.push({
              dataset: 'fct_proform_submission_answer',
              measure: {
                type: 'count',
                field: '*',
              },
              filters: [
                ...this.filters,
                {
                  codename: 'proform_form_id',
                  value: [question.proform_form_id.toString()],
                },
                {
                  codename: 'proform_question_id',
                  value: [question.id.toString()],
                },
              ],
              page: 0,
              per_page: 10,
            });
          }
        });
      });
    }
  }

  createQuizSummaryParams() {
    if (this.selectedProForm) {
      this.quizSummaryParams = {
        id: 'proform-question-quiz',
        dimension: 'grade',
        dataset: 'fct_proform_submission',
        measure: {
          type: 'count',
          field: '*',
        },
        filters: [
          ...this.filters,
          {
            codename: 'proform_form_id',
            value: [this.selectedProForm.id.toString()],
          },
        ],
      };
    }
  }

  getQuizSubmissions() {
    if (this.selectedProForm) {
      this.formSubmissionsParams = {
        id: 'proform-question-quiz',
        report: 'proformSubmissions',
        dataset: 'fct_proform_submission',
        measure: {
          type: 'count',
          field: '*',
        },
        filters: [
          ...this.filters,
          {
            codename: 'proform_form_id',
            value: [this.selectedProForm.id.toString()],
          },
        ],
      };
    }
  }

  getApiResponseEvent(event: ReportApiResponse) {
    if (event.denominator_count && event.denominator_count > 0) {
      this.denominatorCount = event.denominator_count;
    }
    if (event.num_blank_form_submissions) {
      this.blankSubmissionScount = event.num_blank_form_submissions;
    }
  }

  getCalibrationReport() {
    this.formHasCalibration = false;
    if (this.selectedProForm) {
      if (this.selectedProForm.type === ProFormType.CALIBRATION) {
        this.formHasCalibration = true;
      }

      this.calibrationRequestParams = {
        dataset: '',
        filters: [...this.filters],
        page: 0,
        per_page: 10,
      };
    }
  }

  // eslint-disable-next-line class-methods-use-this
  stripHtml(text: string): string {
    return stripHtmlForId(text);
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
